import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import mermaid from "mermaid";
import { Code2, Maximize2, Minus, Plus } from "lucide-react";
// We'll use dynamic import for svg-pan-zoom

// Initialize mermaid with defaults - Japanese aesthetic
mermaid.initialize({
  startOnLoad: true,
  theme: "neutral",
  securityLevel: "loose",
  suppressErrorRendering: true,
  logLevel: "error",
  maxTextSize: 100000, // Increase text size limit
  htmlLabels: true,
  flowchart: {
    htmlLabels: true,
    curve: "basis",
    nodeSpacing: 60,
    rankSpacing: 60,
    padding: 20,
    // Render the SVG at its INTRINSIC size (no forced width:100%), so our CSS
    // max-w/max-h can contain it cleanly. width:100% was collapsing to 0 inside
    // content-width wrappers, making the diagram disappear.
    useMaxWidth: false,
  },
  themeCSS: `
    /* Japanese aesthetic styles for all diagrams */
    .node rect, .node circle, .node ellipse, .node polygon, .node path {
      fill: #f1f5f9;
      stroke: #cbd5e1;
      stroke-width: 1.5px;
      rx: 8px;
      ry: 8px;
    }
    .edgePath .path {
      stroke: #94a3b8;
      stroke-width: 1.5px;
    }
    .edgeLabel {
      background-color: transparent;
      color: #333333;
      p {
        background-color: transparent !important;
      }
    }
    .label {
      color: #333333;
    }
    .cluster rect {
      fill: #f8f4e6;
      stroke: #d7c4bb;
      stroke-width: 1px;
    }

    /* Sequence diagram specific styles */
    .actor {
      fill: #f8f4e6;
      stroke: #d7c4bb;
      stroke-width: 1px;
    }
    text.actor {
      fill: #333333;
      stroke: none;
    }
    .messageText {
      fill: #333333;
      stroke: none;
    }
    .messageLine0, .messageLine1 {
      stroke: #9b7cb9;
    }
    .noteText {
      fill: #333333;
    }

    /* Premium theme overrides - will be applied with data-theme="premium" */
    [data-theme="premium"] .node rect,
    [data-theme="premium"] .node circle,
    [data-theme="premium"] .node ellipse,
    [data-theme="premium"] .node polygon,
    [data-theme="premium"] .node path {
      fill: #222222;
      stroke: #5d4037;
    }
    [data-theme="premium"] .edgePath .path {
      stroke: #9370db;
    }
    [data-theme="premium"] .edgeLabel {
      background-color: transparent;
      color: #f0f0f0;
    }
    [data-theme="premium"] .label {
      color: #f0f0f0;
    }
    [data-theme="premium"] .cluster rect {
      fill: #222222;
      stroke: #5d4037;
    }
    [data-theme="premium"] .flowchart-link {
      stroke: #9370db;
    }

    /* Premium theme sequence diagram overrides */
    [data-theme="premium"] .actor {
      fill: #222222;
      stroke: #5d4037;
    }
    [data-theme="premium"] text.actor {
      fill: #f0f0f0;
      stroke: none;
    }
    [data-theme="premium"] .messageText {
      fill: #f0f0f0;
      stroke: none;
      font-weight: 500;
    }
    [data-theme="premium"] .messageLine0, [data-theme="premium"] .messageLine1 {
      stroke: #9370db;
      stroke-width: 1.5px;
    }
    [data-theme="premium"] .noteText {
      fill: #f0f0f0;
    }
    /* Additional styles for sequence diagram text */
    [data-theme="premium"] #sequenceNumber {
      fill: #f0f0f0;
    }
    [data-theme="premium"] text.sequenceText {
      fill: #f0f0f0;
      font-weight: 500;
    }
    [data-theme="premium"] text.loopText, [data-theme="premium"] text.loopText tspan {
      fill: #f0f0f0;
    }
    /* Add a subtle background to message text for better readability */
    [data-theme="premium"] .messageText, [data-theme="premium"] text.sequenceText {
      paint-order: stroke;
      stroke: #1a1a1a;
      stroke-width: 2px;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    /* Force text elements to be properly colored */
    text[text-anchor][dominant-baseline],
    text[text-anchor][alignment-baseline],
    .nodeLabel,
    .edgeLabel,
    .label,
    text {
      fill: #777 !important;
    }

    [data-theme="premium"] text[text-anchor][dominant-baseline],
    [data-theme="premium"] text[text-anchor][alignment-baseline],
    [data-theme="premium"] .nodeLabel,
    [data-theme="premium"] .edgeLabel,
    [data-theme="premium"] .label,
    [data-theme="premium"] text {
      fill: #f0f0f0 !important;
    }

    /* Add clickable element styles with subtle transitions */
    .clickable {
      transition: all 0.3s ease;
    }
    .clickable:hover {
      transform: scale(1.03);
      cursor: pointer;
    }
    .clickable:hover > * {
      filter: brightness(0.95);
    }
  `,
  // Concrete font (NOT a CSS variable): Mermaid measures label width to size the
  // node boxes, and it cannot resolve `var(--font-…)` for measurement — it then
  // renders a wider font than it measured, clipping the last characters. A real
  // system font stack makes measure == render, so labels no longer truncate.
  fontFamily:
    'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontSize: 13,
});

interface MermaidProps {
  chart: string;
  className?: string;
  zoomingEnabled?: boolean;
}

const MODAL_MIN_ZOOM = 0.25;
const MODAL_MAX_ZOOM = 10;

// Drag-to-pan for an overflow-auto scroll container: press and drag to scroll the
// diagram in any direction (grab / grabbing cursor). Returns a ref + mouse handlers.
function useDragScroll() {
  const ref = useRef<HTMLDivElement>(null);
  const drag = useRef({ down: false, x: 0, y: 0, sl: 0, st: 0 });
  const onMouseDown = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    drag.current = { down: true, x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
    el.style.cursor = "grabbing";
  };
  const onMouseMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el || !drag.current.down) return;
    el.scrollLeft = drag.current.sl - (e.clientX - drag.current.x);
    el.scrollTop = drag.current.st - (e.clientY - drag.current.y);
  };
  const stop = () => {
    const el = ref.current;
    drag.current.down = false;
    if (el) el.style.cursor = "grab";
  };
  return {
    ref,
    style: { cursor: "grab" as const },
    onMouseDown,
    onMouseMove,
    onMouseUp: stop,
    onMouseLeave: stop,
  };
}

// Full screen modal component for the diagram
const FullScreenModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ isOpen, onClose, children }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const pan = useDragScroll();
  const contentRef = useRef<HTMLDivElement>(null);
  // The diagram opens fitted to the modal, which for a big one is a fraction of its real size, so zoom must go far
  // past 200% for its text to be readable: steps of x1.25 up to MODAL_MAX_ZOOM, and 1:1 jumps straight to real size.
  const zoomTo = (z: number) => setZoom(Math.min(MODAL_MAX_ZOOM, Math.max(MODAL_MIN_ZOOM, +z.toFixed(2))));
  const actualSize = () => {
    const svg = contentRef.current?.querySelector("svg");
    const natural = svg?.viewBox?.baseVal?.width;
    const shown = svg?.getBoundingClientRect().width;
    if (natural && shown) zoomTo((natural / (shown / zoom)));
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Handle click outside to close
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  // Reset zoom when modal opens
  useEffect(() => {
    if (isOpen) {
      setZoom(1);
    }
  }, [isOpen]);

  // Ctrl/Cmd + wheel (and a trackpad pinch, which the browser reports the same way) zooms the diagram, not the page.
  useEffect(() => {
    const el = pan.ref.current;
    if (!isOpen || !el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => Math.min(MODAL_MAX_ZOOM, Math.max(MODAL_MIN_ZOOM, +(z * (e.deltaY < 0 ? 1.1 : 1 / 1.1)).toFixed(2))));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [isOpen, pan.ref]);

  if (!isOpen || typeof document === "undefined") return null;

  // Portal to <body> so the fixed overlay escapes the side-panel's stacking
  // context (a transformed/overflow ancestor was trapping it, so it didn't
  // cover the viewport).
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75 p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-2xl w-[92vw] h-[88vh] overflow-hidden flex flex-col"
      >
        {/* Modal header with controls */}
        <div
          className="flex items-center justify-between p-4 border-b border-surface-border"
          style={{ backgroundColor: "#ffffff", color: "#334155" }}
        >
          <div className="font-medium text-text-primary">
            Diagram
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => zoomTo(zoom / 1.25)}
                className="text-muted hover:bg-surface-hover p-2 rounded-md border border-surface-border transition-colors"
                aria-label="Zoom out"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
              </button>
              <span className="text-base text-muted">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={() => zoomTo(zoom * 1.25)}
                aria-label="Zoom in"
              >
                <Plus />
              </button>
              <button
                onClick={actualSize}
                className="text-muted hover:bg-surface-hover px-2 py-1 rounded-md border border-surface-border text-sm transition-colors"
                aria-label="Actual size"
                title="Actual size (1:1)"
              >
                1:1
              </button>
              <button
                onClick={() => setZoom(1)}
                className="text-muted hover:bg-surface-hover p-2 rounded-md border border-surface-border transition-colors"
                aria-label="Reset zoom"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"></path>
                  <path d="M21 3v5h-5"></path>
                </svg>
              </button>
            </div>
            <button onClick={onClose} aria-label="Close">
              <Minus />
            </button>
          </div>
        </div>

        {/* Scrollable viewport: diagram fits by default, +/- zooms, and when
            zoomed past the modal you can scroll it in every direction. */}
        <div
          ref={pan.ref}
          style={pan.style}
          onMouseDown={pan.onMouseDown}
          onMouseMove={pan.onMouseMove}
          onMouseUp={pan.onMouseUp}
          onMouseLeave={pan.onMouseLeave}
          className="overflow-auto p-6 flex-1 bg-surface-hover flex items-start justify-center"
        >
          <div
            ref={contentRef}
            className="shrink-0"
            // CSS `zoom` scales layout so the body gets real overflow → drag-pan
            // works in all directions (transform:scale wouldn't create scroll room).
            style={{ zoom } as React.CSSProperties}
          >
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Mermaid: React.FC<MermaidProps> = ({
  chart,
  className = "",
  zoomingEnabled = false,
}) => {
  const [svg, setSvg] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Toolbar: toggle between the rendered diagram and its Mermaid source.
  const [showSource, setShowSource] = useState(false);
  const mermaidRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const idRef = useRef(`mermaid-${Math.random().toString(36).substring(2, 9)}`);

  // Inline zoom via a plain CSS transform — reliable and dependency-free (the
  // svg-pan-zoom instance was intermittently null, so the +/- pill did nothing).
  const [inlineZoom, setInlineZoom] = useState(1);
  const zoomBy = (factor: number) =>
    setInlineZoom((z) => Math.min(MODAL_MAX_ZOOM, Math.max(0.4, +(z * factor).toFixed(2))));
  const inlinePan = useDragScroll();
  // The diagram's fit width, in px: the box's width at the time it renders. Capping the SVG at 100% instead tied it
  // to the box, so zooming in stopped growing it once it reached the box edges; a px cap scales with the zoom, and
  // the wrapper (w-max) may then outgrow the box, which scrolls.
  const fitRef = useRef<HTMLDivElement>(null);
  const [fitWidth, setFitWidth] = useState(0);
  useEffect(() => {
    const box = fitRef.current?.parentElement;
    if (!box) return;
    const measure = () => {
      const style = getComputedStyle(box);
      setFitWidth(box.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    return () => observer.disconnect();
  }, [svg]);

  // Reset zoom whenever a new diagram renders.
  useEffect(() => {
    setInlineZoom(1);
  }, [svg]);

  useEffect(() => {
    if (!chart) return;

    let isMounted = true;

    const renderChart = async () => {
      if (!isMounted) return;

      try {
        setError(null);
        setSvg("");

        // Render the chart directly without preprocessing
        const { svg: renderedSvg } = await mermaid.render(idRef.current, chart);

        if (!isMounted) return;

        // The light background comes from the white wrapper divs (inline style),
        // NOT from patching the SVG — injecting a second `style` attr onto <svg>
        // dropped Mermaid's own `style="max-width:…"`, collapsing the diagram.
        setSvg(renderedSvg);

        // Call mermaid.contentLoaded to ensure proper initialization
        setTimeout(() => {
          mermaid.contentLoaded();
        }, 50);
      } catch (err) {
        console.error("Mermaid rendering error:", err);

        const errorMessage = err instanceof Error ? err.message : String(err);

        if (isMounted) {
          setError(`Failed to render diagram: ${errorMessage}`);

          if (mermaidRef.current) {
            mermaidRef.current.innerHTML = `
              <div class="ext-red-400 text-sm mb-1">Syntax error in diagram</div>
              <pre class="text-sm overflow-auto p-2 bg-surface rounded">${chart}</pre>
            `;
          }
        }
      }
    };

    renderChart();

    return () => {
      isMounted = false;
    };
  }, [chart]);

  const handleDiagramClick = () => {
    if (!error && svg) {
      setIsFullscreen(true);
    }
  };

  if (error) {
    return (
      <div
        className={`border border-[var(--highlight)]/30 rounded-md p-4 bg-[var(--highlight)]/5 ${className}`}
      >
        <div className="flex items-center mb-3">
          <div className="text-[var(--highlight)] text-sm font-medium flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            Diagram rendering error
          </div>
        </div>
        <div ref={mermaidRef} className="text-sm overflow-auto"></div>
        <div className="mt-3 text-sm text-[var(--muted)] font-serif">
          There is a syntax error in the diagram, and it cannot be rendered.
        </div>
      </div>
    );
  }

  if (!svg) {
    return (
      <div className={`flex justify-center items-center p-4 ${className}`}>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-[var(--accent-primary)]/70 rounded-full animate-pulse"></div>
          <div className="w-2 h-2 bg-[var(--accent-primary)]/70 rounded-full animate-pulse delay-75"></div>
          <div className="w-2 h-2 bg-[var(--accent-primary)]/70 rounded-full animate-pulse delay-150"></div>
          <span className="text-[var(--muted)] text-sm ml-2 font-serif">
            Rendering diagram...
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        ref={containerRef}
        className={`w-full max-w-full ${zoomingEnabled ? "h-[600px] p-4" : ""}`}
      >
        <div
          className={`relative group ${
            zoomingEnabled ? "h-full rounded-xl border border-surface-border" : ""
          }`}
          style={zoomingEnabled ? { backgroundColor: "#ffffff" } : undefined}
        >
          {/* Toolbar: toggle Mermaid source (</>) and open fullscreen (⤢). */}
          {zoomingEnabled && (
            <div className="absolute top-2.5 right-2.5 z-10 flex items-center gap-0.5 rounded-lg border border-surface-border bg-white/95 px-0.5 py-0.5 shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-800/95">
              <button
                type="button"
                aria-label={showSource ? "Show diagram" : "Show code"}
                title={showSource ? "Show diagram" : "Show code"}
                onClick={() => setShowSource((s) => !s)}
                style={{ backgroundColor: showSource ? "#f1f5f9" : "transparent", color: "#94a3b8" }}
                className="flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:!bg-surface-hover"
              >
                <Code2 className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                aria-label="Fullscreen"
                title="Fullscreen"
                onClick={() => setIsFullscreen(true)}
                style={{ backgroundColor: "transparent", color: "#94a3b8" }}
                className="flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:!bg-surface-hover"
              >
                <Maximize2 className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {showSource ? (
            <pre className={`overflow-auto rounded-md bg-surface-hover p-4 text-left text-sm text-text-primary ${zoomingEnabled ? "h-full" : "my-2"}`}>
              <code>{chart}</code>
            </pre>
          ) : (
            <div
              ref={zoomingEnabled ? inlinePan.ref : undefined}
              className={`overflow-auto rounded-md ${className} ${
                zoomingEnabled ? "h-full p-4" : "my-2"
              }`}
              style={{
                backgroundColor: "#ffffff",
                ...(zoomingEnabled ? { cursor: "grab" } : {}),
              }}
              onMouseDown={zoomingEnabled ? inlinePan.onMouseDown : undefined}
              onMouseMove={zoomingEnabled ? inlinePan.onMouseMove : undefined}
              onMouseUp={zoomingEnabled ? inlinePan.onMouseUp : undefined}
              onMouseLeave={zoomingEnabled ? inlinePan.onMouseLeave : undefined}
              onClick={zoomingEnabled ? undefined : handleDiagramClick}
              title={zoomingEnabled ? undefined : "Click to view fullscreen"}
            >
              {/* Default: the WHOLE diagram fits the box (contain via max-h/max-w).
                  h-full on this wrapper gives max-h-full something to resolve
                  against; the +/- pill then scales it via transform. */}
              <div
                ref={fitRef}
                className="flex justify-center w-max min-w-full [&_svg]:h-auto [&_svg]:w-auto [&_svg]:max-h-[520px] [&_svg]:max-w-[var(--fit-w)]"
                // CSS `zoom` scales the LAYOUT (unlike transform:scale), so zooming
                // creates real overflow and drag-pan works in every direction. A
                // fixed max-h (not max-h-full) fits the box without collapsing.
                style={{ zoom: inlineZoom, "--fit-w": fitWidth ? `${fitWidth}px` : "100%" } as React.CSSProperties}
                dangerouslySetInnerHTML={{ __html: svg }}
              />
            </div>
          )}

          {/* Clean +/- zoom pill (replaces svg-pan-zoom's grey blocks). Drives the
              stored pan-zoom instance; stepped zoom feels slow/gentle. */}
          {zoomingEnabled && !showSource && (
            <div
              className="absolute bottom-3 right-3 z-10 flex flex-col overflow-hidden rounded-full border border-surface-border shadow-sm"
              style={{ backgroundColor: "#ffffff" }}
            >
              <button
                type="button"
                aria-label="Zoom in"
                onClick={() => zoomBy(1.25)}
                style={{ backgroundColor: "#ffffff", color: "#64748b" }}
                className="flex h-7 w-7 items-center justify-center hover:!bg-surface-hover transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
              <div className="h-px w-full bg-surface-hover" />
              <button
                type="button"
                aria-label="Zoom out"
                onClick={() => zoomBy(0.8)}
                style={{ backgroundColor: "#ffffff", color: "#64748b" }}
                className="flex h-7 w-7 items-center justify-center hover:!bg-surface-hover transition-colors"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {!zoomingEnabled && (
            <div className="absolute top-2 right-2 bg-surface/70 dark:bg-surface/70 text-white p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-1.5 text-sm shadow-md pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                <line x1="11" y1="8" x2="11" y2="14"></line>
                <line x1="8" y1="11" x2="14" y2="11"></line>
              </svg>
              <span>Click to zoom</span>
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen modal is available in both modes: click-to-open when zoom is
          off, and via the ⤢ toolbar button when zoom is on. */}
      <FullScreenModal
        isOpen={isFullscreen}
        onClose={() => setIsFullscreen(false)}
      >
        {/* Fit the WHOLE diagram in the modal by default (contain via viewport
            caps); the +/- zoom then scales it and the body scrolls to pan. */}
        <div
          className="[&_svg]:h-auto [&_svg]:max-h-[74vh] [&_svg]:w-auto [&_svg]:!max-w-[84vw]"
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </FullScreenModal>
    </>
  );
};

export default Mermaid;
